import type { ReactNode } from "react";

export type BriefingBlockKind =
  "briefTable" | "priceLevels" | "sourceTrail" | "decisionSummary";

export interface BriefingBlock {
  kind: BriefingBlockKind;
  payload: Record<string, unknown>;
}

interface SourceRef {
  label: string;
  detail?: string;
  url?: string;
  tier?: string;
}

export function isBriefingBlockKind(
  value: unknown,
): value is BriefingBlockKind {
  return (
    value === "briefTable" ||
    value === "priceLevels" ||
    value === "sourceTrail" ||
    value === "decisionSummary"
  );
}

export function renderBriefingBlock(block: BriefingBlock): ReactNode {
  if (block.kind === "briefTable")
    return <BriefTable payload={block.payload} />;
  if (block.kind === "priceLevels")
    return <PriceLevels payload={block.payload} />;
  if (block.kind === "sourceTrail")
    return <SourceTrail payload={block.payload} />;
  return <DecisionSummary payload={block.payload} />;
}

export function labelForBriefingKind(kind: BriefingBlockKind): string {
  if (kind === "priceLevels") return "brief price levels";
  if (kind === "sourceTrail") return "brief sources";
  if (kind === "decisionSummary") return "brief decision";
  return "brief table";
}

function BriefTable({ payload }: { payload: Record<string, unknown> }) {
  const columns = readStringList(payload.columns, ["Source", "Signal"]);
  const rows = normalizeRows(payload.rows, columns.length);
  return (
    <section className="document-rich__brief-table">
      <BriefHeader payload={payload} fallback="Brief table" meta="table" />
      <div className="document-rich__table-wrap">
        <table className="document-rich__table document-rich__table--compact">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.join("|")}-${index}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`${cell}-${cellIndex}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SourceRefs payload={payload} />
    </section>
  );
}

function PriceLevels({ payload }: { payload: Record<string, unknown> }) {
  const levels = readRecordList(payload.levels).slice(0, 8);
  return (
    <section className="document-rich__price-levels">
      <BriefHeader payload={payload} fallback="Price levels" meta="levels" />
      <div className="document-rich__level-list">
        {levels.map((level, index) => (
          <div key={`${readString(level.label, "Level")}-${index}`}>
            <span>{readString(level.label, "Level")}</span>
            <strong>{readString(level.value, "pending")}</strong>
            <small>{readString(level.source, "Source required")}</small>
          </div>
        ))}
      </div>
      <SourceRefs payload={payload} />
    </section>
  );
}

function SourceTrail({ payload }: { payload: Record<string, unknown> }) {
  const sources = normalizeSources(payload.sources);
  return (
    <section className="document-rich__source-trail">
      <BriefHeader payload={payload} fallback="Source trail" meta="sources" />
      <div className="document-rich__source-list">
        {sources.map((source, index) => (
          <SourceItem key={`${source.label}-${index}`} source={source} />
        ))}
      </div>
    </section>
  );
}

function DecisionSummary({ payload }: { payload: Record<string, unknown> }) {
  const actions = readStringList(payload.actions, []);
  const contextIds = readRecord(payload.contextIds);
  return (
    <section className="document-rich__decision-summary">
      <BriefHeader payload={payload} fallback="Decision summary" meta="brief" />
      <p>{readString(payload.summary, "No decision summary available.")}</p>
      {actions.length > 0 ? (
        <ul>
          {actions.map((action) => (
            <li key={action}>{action}</li>
          ))}
        </ul>
      ) : null}
      {Object.keys(contextIds).length > 0 ? (
        <div className="document-rich__context-ids">
          {Object.entries(contextIds).map(([key, value]) =>
            value ? (
              <span key={key}>
                {key}: {String(value)}
              </span>
            ) : null,
          )}
        </div>
      ) : null}
      <SourceRefs payload={payload} />
    </section>
  );
}

function BriefHeader({
  payload,
  fallback,
  meta,
}: {
  payload: Record<string, unknown>;
  fallback: string;
  meta: string;
}) {
  return (
    <header className="document-rich__header">
      <h3>{readString(payload.title, fallback)}</h3>
      <span>{readString(payload.stance, meta)}</span>
    </header>
  );
}

function SourceRefs({ payload }: { payload: Record<string, unknown> }) {
  const sources = normalizeSources(payload.sourceRefs);
  if (sources.length === 0) return null;
  return (
    <div className="document-rich__source-list document-rich__source-list--inline">
      {sources.map((source, index) => (
        <SourceItem key={`${source.label}-${index}`} source={source} compact />
      ))}
    </div>
  );
}

function SourceItem({
  source,
  compact,
}: {
  source: SourceRef;
  compact?: boolean;
}) {
  return (
    <p className="document-rich__source-item">
      <span>{source.label}</span>
      {source.tier ? <small>{source.tier}</small> : null}
      {!compact && source.detail ? <em>{source.detail}</em> : null}
      {source.url ? (
        <a href={source.url} target="_blank" rel="noreferrer">
          {source.url}
        </a>
      ) : null}
    </p>
  );
}

function normalizeRows(value: unknown, width: number): string[][] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row): row is unknown[] => Array.isArray(row))
    .map((row) =>
      Array.from({ length: width }, (_item, index) => String(row[index] ?? "")),
    );
}

function normalizeSources(value: unknown): SourceRef[] {
  return readRecordList(value)
    .map((source) => ({
      label: readString(source.label, ""),
      detail: readString(source.detail, ""),
      url: readString(source.url, ""),
      tier: readString(source.tier, ""),
    }))
    .filter((source) => source.label)
    .slice(0, 10);
}

function readRecordList(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord);
}

function readRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function readString(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function readStringList(value: unknown, fallback: string[]): string[] {
  if (!Array.isArray(value)) return fallback;
  const list = value
    .map(String)
    .map((item) => item.trim())
    .filter(Boolean);
  return list.length > 0 ? list : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}
